import { User } from "~/common/Store/models/User";
import isActiveSale from "./isActiveSale";
import shouldDisplayRequiredCustomization from "./shouldDisplayRequiredCustomization";
import shouldDisplayBelowMapPrice from "./shouldDisplayBelowMapPrice";
import shouldDisplayQuantityDiscounts from "./shouldDisplayQuantityDiscounts";
import getLowestPriceGroupMember from "./getLowestPriceGroupMember";

export interface PricingGroupMember {
  optionLabel: string;
  displayName: string;
  itemNumber: string;
  itemNumberId: number;
  unitOfMeasurement: string;
  configurationOptions: { name: string; value: string }[];
  price: LowestPricingProduct["price"];
  title: string;
  isInStock: boolean;
  minimumBuy: number;
  mustBuy: number;
  startBuy: number;
  mustPurchaseMoreThanOne: boolean;
  primaryImagePath: string;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
}

export interface LowestPricingProduct {
  customizationRequired?: boolean;
  customizationUrl?: string;
  isCustomizable?: boolean;
  minimumCustomizationPrice?: number;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
  startBuy?: number;
  groupingAttributes?: {
    isVirtual?: boolean;
    isGroupMaster: boolean;
    groupMembers?: PricingGroupMember[];
  };
  price: {
    minimumAdvertisedPriceProperties: {
      price: number;
      type: string;
    };
    minimumAdvertisedPriceOverride: {
      priceLevel5: number;
    };
    quantityDiscounts: {
      endQuantity?: number;
      isLotDiscount: boolean;
      quantity: number;
      price: number;
      startQuantity: number;
    }[];
    displaySalePrice: boolean;
    saleStartDate?: string;
    saleEndDate?: string;
    salePrice: number;
    price: number;
    displayMinimumAdvertisedPrice: boolean;
  };
}

export const getLowestDisplayPrice = (
  product: LowestPricingProduct,
  user: User,
  excludeQuantityDiscounts?: boolean,
  excludePlusPrice?: boolean,
  isGoogleView?: boolean,
): number => {
  const { price, minimumCustomizationPrice = 0, startBuy = 1 } = product;

  if (
    shouldDisplayRequiredCustomization({
      customizationRequired: product.customizationRequired ?? false,
      customizationUrl: product.customizationUrl ?? "",
      isCustomizable: product.isCustomizable ?? false,
    })
  ) {
    return minimumCustomizationPrice;
  }

  // p1
  const prices = [price.price];

  // sale price
  if (price.salePrice && isActiveSale(price)) {
    prices.push(price.salePrice);
  }

  // vg master price
  if (
    product.groupingAttributes &&
    product.groupingAttributes.isGroupMaster &&
    product.groupingAttributes.groupMembers &&
    product.groupingAttributes.groupMembers.length > 1
  ) {
    const lowerPriceGroupMember = getLowestPriceGroupMember(
      product.groupingAttributes.groupMembers,
      user,
    );
    prices.push(lowerPriceGroupMember.price.price);
  }

  // p4
  if (
    !!price.minimumAdvertisedPriceProperties?.type?.trim() &&
    price.minimumAdvertisedPriceProperties.price &&
    (price.minimumAdvertisedPriceProperties.type.trim() === "G" ||
      shouldDisplayBelowMapPrice(product, user))
  ) {
    prices.push(price.minimumAdvertisedPriceProperties.price);
  }

  // p5
  if (
    !excludePlusPrice &&
    !!price.minimumAdvertisedPriceOverride?.priceLevel5 &&
    user.hasWebPlus
  ) {
    prices.push(price.minimumAdvertisedPriceOverride.priceLevel5);
  }

  // quantity discounts
  const [
    displayQuantityDiscounts,
    quantityDiscountsWithQuantitiesGreaterThanStartQuantity,
  ] = shouldDisplayQuantityDiscounts(product, Math.min(...prices), user);

  if (isGoogleView && !user.registered) {
    return Math.min(...prices);
  }

  if (excludeQuantityDiscounts || !price.quantityDiscounts?.length) {
    return Math.min(...prices);
  }

  if (displayQuantityDiscounts) {
    const quantityDiscountsSet = new Set(
      quantityDiscountsWithQuantitiesGreaterThanStartQuantity,
    );

    const minStartQuantity = Math.min(
      ...Array.from(quantityDiscountsSet).map((d) => d.startQuantity),
    );
    // Check if any quantity discounts were excluded because their startQuantity is less than startBuy
    const nonQualifiedDiscountsWithStartQuantityLowerThanStartBuy =
      price.quantityDiscounts.filter(
        (discount) =>
          !quantityDiscountsSet.has(discount) &&
          minStartQuantity > discount.startQuantity &&
          discount.price < Math.min(...prices),
      );

    // If there are any non-qualified discounts with a startQuantity lower than startBuy, include their prices for lowest price calculation
    if (
      nonQualifiedDiscountsWithStartQuantityLowerThanStartBuy &&
      nonQualifiedDiscountsWithStartQuantityLowerThanStartBuy.length > 0
    ) {
      prices.push(
        nonQualifiedDiscountsWithStartQuantityLowerThanStartBuy[
          nonQualifiedDiscountsWithStartQuantityLowerThanStartBuy.length - 1
        ]?.price ?? Infinity,
      );
    }
  }
  // If all quantity discounts have a start quantity less than startBuy, displayQuantityDiscounts is false and quantityDiscountsWitQuantitiesGreaterThanStartQuantity is empty.
  // In this scenario, add the lowest price from the available quantity discounts to the prices array for comparison.
  else if (
    quantityDiscountsWithQuantitiesGreaterThanStartQuantity.length === 0 &&
    price.quantityDiscounts.length > 0
  ) {
    const isAllQualifiedDiscountsWithStartQuantityLowerThanStartBuy =
      price.quantityDiscounts.every(
        (discount) =>
          startBuy >= discount.startQuantity &&
          discount.price < Math.min(...prices),
      );
    if (isAllQualifiedDiscountsWithStartQuantityLowerThanStartBuy) {
      price.quantityDiscounts = price.quantityDiscounts.sort(
        (a, b) => b.price - a.price,
      );
      prices.push(
        price.quantityDiscounts[price.quantityDiscounts.length - 1]?.price ??
          Infinity,
      );
    }
  }

  return Math.min(...prices);
};

export default getLowestDisplayPrice;
